<template>
  <div class="aliados-page">

    <!-- ─── Hero ─── -->
    <section class="aliados-hero">
      <div class="container">
        <router-link to="/" class="aliados-logo" aria-label="Ir a Neliel Papelería">
          <img src="https://neliel.mx/img/logo_neliel-naranja.png" alt="Neliel Papelería" />
        </router-link>
        <span class="aliados-chip"><i class="fas fa-handshake"></i> Red Neliel</span>
        <h1 class="aliados-title">Nuestros <em>aliados</em></h1>
        <p class="aliados-desc">Negocios con los que trabajamos de la mano. Visítalos y apoya lo local.</p>
      </div>
    </section>

    <!-- ─── Aliados ─── -->
    <section class="aliados-section">
      <div class="container">

        <div class="aliados-grid" v-if="loading">
          <div class="skeleton-card" v-for="n in 3" :key="n"></div>
        </div>

        <div class="empty-state" v-else-if="error">
          <i class="fas fa-wifi"></i>
          <p>{{ error }}</p>
        </div>

        <div class="empty-state" v-else-if="aliados.length === 0">
          <i class="fas fa-handshake"></i>
          <p>Pronto conocerás a nuestros aliados. ¡Vuelve pronto!</p>
        </div>

        <div class="aliados-grid" v-else>
          <a
            v-for="(aliado, i) in aliados"
            :key="aliado.id"
            :href="aliado.url"
            target="_blank"
            rel="noopener noreferrer"
            class="aliado-card"
          >
            <div class="aliado-visual" :class="'aliado-visual--' + variante(i)">
              <img v-if="aliado.imagen" :src="aliado.imagen" :alt="aliado.nombre" loading="lazy" />
              <i v-else :class="aliado.icono || 'fas fa-store'" class="aliado-icon"></i>
            </div>
            <div class="aliado-body">
              <span v-if="aliado.categoria" class="aliado-cat">{{ aliado.categoria }}</span>
              <h3>{{ aliado.nombre }}</h3>
              <p v-if="aliado.descripcion" class="aliado-desc">{{ aliado.descripcion }}</p>
              <div class="aliado-footer">
                <span class="aliado-link">
                  Visitar sitio <i class="fas fa-external-link-alt"></i>
                </span>
              </div>
            </div>
          </a>
        </div>

      </div>
    </section>

    <!-- ─── Pie ─── -->
    <div class="aliados-back">
      <router-link to="/" class="btn btn-ghost btn-sm">
        <i class="fas fa-arrow-left"></i> Volver a Neliel
      </router-link>
    </div>

  </div>
</template>

<script>
// JSON publicado aparte del build (public/aliados/aliados.json), generado desde
// el Excel con `npm run aliados`. Actualizar aliados = subir este JSON y los logos.
const API_ALIADOS = `${process.env.BASE_URL}aliados/aliados.json`

const VARIANTES = ['teal', 'coral', 'peach']

export default {
  name: 'NegociosAliados',
  data() {
    return {
      aliados: [],
      loading: true,
      error:   null,
    }
  },
  async mounted() {
    this.ponerMeta()
    try {
      // no-cache: el navegador revalida con el servidor, así un JSON recién subido se ve de inmediato
      const res = await fetch(API_ALIADOS, { cache: 'no-cache' })
      if (!res.ok) throw new Error('Error en la respuesta')
      const data = await res.json()
      this.aliados = Array.isArray(data) ? data : []
    } catch {
      this.error = 'No se pudieron cargar los aliados. Intenta más tarde.'
    } finally {
      this.loading = false
    }
  },
  beforeUnmount() {
    if (this.robotsMeta) this.robotsMeta.remove()
    document.title = this.tituloPrevio
  },
  methods: {
    ponerMeta() {
    // Página accesible solo por URL directa: se pide a buscadores no indexarla
    this.robotsMeta = document.createElement('meta')
    this.robotsMeta.name = 'robots'
    this.robotsMeta.content = 'noindex, nofollow'
    document.head.appendChild(this.robotsMeta)

    this.tituloPrevio = document.title
    document.title = 'Aliados · Neliel Papelería'
    },
    variante(i) {
      return VARIANTES[i % VARIANTES.length]
    },
  },
}
</script>

<style scoped>
.aliados-page {
  font-family: var(--f-body);
  color: var(--c-text);
  background: var(--c-bg);
  min-height: 100vh;
}

/* ── Hero ── */
.aliados-hero {
  background: var(--c-primary);
  padding: 56px 0 72px;
  position: relative;
  overflow: hidden;
  text-align: center;
}
.aliados-hero .container {
  display: flex; flex-direction: column; align-items: center;
  position: relative; z-index: 1;
}
.aliados-hero::before {
  content: '';
  position: absolute; top: -120px; right: -120px;
  width: 320px; height: 320px; border-radius: 50%;
  background: radial-gradient(circle, rgba(237,143,123,0.25) 0%, transparent 70%);
  pointer-events: none;
}
.aliados-hero::after {
  content: '';
  position: absolute; bottom: -2px; left: 0; right: 0; height: 48px;
  background: var(--c-bg);
  clip-path: ellipse(55% 100% at 50% 100%);
}

/* Espacio libre alrededor del logo (manual de marca) */
.aliados-logo { display: inline-block; padding: 12px; margin-bottom: 20px; }
.aliados-logo img { height: 96px; width: auto; display: block; }

.aliados-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.72rem; font-weight: 600; letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.14);
  padding: 5px 14px; border-radius: 999px; margin-bottom: 14px;
}
.aliados-chip i { color: var(--c-cream); }

.aliados-title {
  font-family: var(--f-body);
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: 700; color: #fff;
  line-height: 1.15; margin: 0 0 12px;
}
/* Tipografía secundaria (caligráfica) solo en textos cortos */
.aliados-title em {
  font-family: 'Dancing Script', cursive;
  font-style: normal; font-weight: 700;
  color: var(--c-coral);
}
.aliados-desc {
  font-size: 1rem; color: rgba(255,255,255,0.65);
  max-width: 480px; line-height: 1.6; margin: 0;
}

/* ── Grid ── */
.aliados-section { padding: 48px 0 56px; }
.aliados-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
}

.aliado-card {
  background: var(--c-surface);
  border: 1.5px solid rgba(0,59,76,0.09);
  border-radius: var(--r-lg);
  overflow: hidden;
  text-decoration: none; color: inherit;
  display: flex; flex-direction: column;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
}
.aliado-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
  background: var(--c-coral); z-index: 1;
}
.aliado-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,59,76,0.1); }
.aliado-card:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 2px; }

.aliado-visual {
  aspect-ratio: 16 / 9;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.aliado-visual--teal  { background: linear-gradient(135deg, #003b4c 0%, #005f78 100%); }
.aliado-visual--coral { background: linear-gradient(135deg, #ed8f7b 0%, #f0c2b2 100%); }
.aliado-visual--peach { background: linear-gradient(135deg, #f0c2b2 0%, #f6dfa4 100%); }

.aliado-visual img {
  width: 100%; height: 100%; object-fit: cover;
  background: #fff;
  transition: transform 0.3s;
}
.aliado-card:hover .aliado-visual img { transform: scale(1.05); }

.aliado-icon { font-size: 3.6rem; transition: transform 0.3s; }
.aliado-visual--teal .aliado-icon { color: rgba(255,255,255,0.4); }
.aliado-visual--coral .aliado-icon,
.aliado-visual--peach .aliado-icon { color: rgba(0,59,76,0.3); }
.aliado-card:hover .aliado-icon { transform: scale(1.1); }

.aliado-body { padding: 18px 20px 20px; flex: 1; display: flex; flex-direction: column; }
.aliado-cat {
  align-self: flex-start;
  background: var(--c-cream); color: var(--c-primary);
  font-size: 0.65rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 3px 10px; border-radius: 999px; margin-bottom: 10px;
}
.aliado-body h3 {
  font-size: 1rem; font-weight: 700; color: var(--c-primary);
  margin: 0 0 6px; line-height: 1.3;
}
.aliado-desc {
  font-size: 0.85rem; color: var(--c-text3); line-height: 1.5;
  margin: 0 0 14px; flex: 1;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.aliado-footer {
  border-top: 1px solid rgba(0,59,76,0.07);
  padding-top: 12px; margin-top: auto;
}
.aliado-link {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 0.82rem; font-weight: 700; color: var(--c-coral);
}
.aliado-link i { font-size: 0.72rem; transition: transform 0.2s; }
.aliado-card:hover .aliado-link i { transform: translate(2px, -2px); }

/* ── Cargando ── */
.skeleton-card {
  height: 340px; border-radius: var(--r-lg);
  background: linear-gradient(90deg, #e8e4df 25%, #f0ede9 50%, #e8e4df 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Vacío ── */
.empty-state { text-align: center; padding: 64px 24px; color: var(--c-text3); }
.empty-state i { font-size: 3rem; margin-bottom: 16px; display: block; opacity: 0.4; }

/* ── Pie ── */
.aliados-back { text-align: center; padding: 0 16px 56px; }

@media (max-width: 640px) {
  .container { padding: 0 16px; }
  .aliados-hero { padding: 40px 0 60px; }
  .aliados-logo img { height: 72px; }
  .aliados-section { padding: 32px 0 40px; }
  .aliados-grid { grid-template-columns: 1fr; gap: 16px; }
}
</style>
